import { getPollStatus } from "@/shared/utils/polls";
import {
  Poll,
  PollBody,
  PollsResponse,
  PollStatus,
} from "@/shared/utils/interfaces";
import dayjs from "dayjs";

/**
 * Temporary in-memory backend for polls.
 *
 * There is no polls endpoint yet. The hooks in `src/shared/queries/polls` and
 * `src/shared/mutations/polls` call these functions; once the backend exists,
 * swap each hook's function for the real `get2` / `post2` / `put2` / `destroy2`
 * call (see the TODO in every hook) and delete this file.
 *
 * Data lives in memory, so it resets to the seed on a page reload.
 */

const DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm";

const delay = <T>(value: T, ms = 300): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), ms));

const at = (days: number) =>
  dayjs().add(days, "day").startOf("hour").format(DATE_TIME_FORMAT);

let nextPollId = 1;
let nextOptionId = 1;

// Gives new options an id and keeps the id of existing ones
const toPoll = (
  body: PollBody,
  itemId = nextPollId++,
  createdAt = dayjs().format(DATE_TIME_FORMAT)
): Poll => ({
  ...body,
  item_id: itemId,
  created_at: createdAt,
  options: body.options.map((option) => ({
    text: option.text,
    item_id: option.item_id ?? nextOptionId++,
  })),
});

let polls: Poll[] = [
  toPoll(
    {
      question: "Hoe tevreden ben je met het nieuwe kassasysteem?",
      description: "We horen graag hoe het werken met de nieuwe kassa bevalt.",
      can_choose_multiple_options: false,
      start_time: at(-3),
      end_time: at(11),
      options: [
        { text: "Zeer tevreden" },
        { text: "Tevreden" },
        { text: "Neutraal" },
        { text: "Ontevreden" },
      ],
    },
    undefined,
    at(-4)
  ),
  toPoll(
    {
      question: "Welke functies wil je als eerste zien?",
      can_choose_multiple_options: true,
      start_time: at(5),
      end_time: at(19),
      options: [
        { text: "Voorraadbeheer" },
        { text: "Tafelreserveringen" },
        { text: "Loyaliteitsprogramma" },
        { text: "Uitgebreidere rapportages" },
      ],
    },
    undefined,
    at(-1)
  ),
  toPoll(
    {
      question: "Gebruik je de bezorgapp voor je eigen koeriers?",
      can_choose_multiple_options: false,
      start_time: at(-30),
      end_time: at(-16),
      options: [{ text: "Ja" }, { text: "Nee" }],
    },
    undefined,
    at(-31)
  ),
];

const notFound = () =>
  Promise.reject({ response: { status: 404, data: { error_id: "notFound" } } });

interface GetPollsParams {
  search?: string;
  status?: PollStatus;
  limit: number;
  offset: number;
}

export const getMockPolls = ({
  search,
  status,
  limit,
  offset,
}: GetPollsParams): Promise<PollsResponse> => {
  const term = search?.trim().toLowerCase();
  const filtered = polls
    .filter((poll) => !term || poll.question.toLowerCase().includes(term))
    .filter((poll) => !status || getPollStatus(poll) === status)
    // Newest start first
    .sort((a, b) => dayjs(b.start_time).valueOf() - dayjs(a.start_time).valueOf());

  return delay({
    total: filtered.length,
    limit,
    offset,
    items: filtered.slice(offset, offset + limit),
  });
};

export const getMockPoll = (id: number): Promise<Poll> => {
  const poll = polls.find((p) => p.item_id === id);
  return poll ? delay(poll) : notFound();
};

export const createMockPoll = (body: PollBody): Promise<Poll> => {
  const poll = toPoll(body);
  polls = [...polls, poll];
  return delay(poll);
};

export const updateMockPoll = (id: number, body: PollBody): Promise<Poll> => {
  const current = polls.find((p) => p.item_id === id);
  if (!current) return notFound();

  const poll = toPoll(body, id, current.created_at);
  polls = polls.map((p) => (p.item_id === id ? poll : p));
  return delay(poll);
};

export const deleteMockPoll = (id: number): Promise<void> => {
  if (!polls.some((p) => p.item_id === id)) return notFound();
  polls = polls.filter((p) => p.item_id !== id);
  return delay(undefined);
};
